import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  ZoomIn as ZoomInIcon,
  Refresh as RefreshIcon,
  PhotoLibrary as PhotoIcon,
} from '@mui/icons-material';

import { SitePhoto, PHOTO_CATEGORY_LABEL, PHOTO_CATEGORIES, isVideoPhoto } from '../../../models/SitePhoto';
import { SitePhotoService } from '../../../services/SitePhotoService';
import PhotoLightboxDialog from '../dialogs/PhotoLightboxDialog';

interface PhotoGalleryViewProps {
  projectId: number;
}

interface Snack {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info';
}

/** 사진의 정렬·그룹화 기준일. taken_at 우선, 없으면 created_at. */
const photoDate = (p: SitePhoto): string => p.taken_at || p.created_at;

/** YYYY-MM-DD 로 잘라낸 그룹 키. */
const dayKey = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

/** 사람에게 보여줄 그룹 헤더 — "오늘 / 어제 / 2026년 5월 18일 (월)". */
const formatDayHeader = (key: string): string => {
  const d = new Date(`${key}T00:00:00`);
  if (Number.isNaN(d.getTime())) return key;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const target = d.getTime();
  const diffDays = Math.round((target - startOfToday) / 86_400_000);
  if (diffDays === 0) return `오늘 (${key})`;
  if (diffDays === -1) return `어제 (${key})`;
  if (diffDays < 0 && diffDays > -7) return `${-diffDays}일 전 (${key})`;
  return d.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'short',
  });
};

/**
 * 현장 사진 갤러리 — 대시보드 사이드바 '사진' 섹션의 인라인 컨텐츠.
 *
 *  - 날짜별(YYYY-MM-DD) 섹션 + 그리드 썸네일
 *  - 다중 파일 업로드 (드래그-드롭 + 파일 피커)
 *  - 썸네일 hover 시 다운로드/삭제 액션
 *  - 클릭 → PhotoLightboxDialog (좌우 화살표/ESC, 다운로드, 새 탭)
 *  - 카테고리 필터 토글
 */
const PhotoGalleryView: React.FC<PhotoGalleryViewProps> = ({ projectId }) => {
  const [photos, setPhotos] = useState<SitePhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadCategory, setUploadCategory] = useState<string>('exterior');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [snack, setSnack] = useState<Snack>({ open: false, message: '', severity: 'success' });
  const [uploading, setUploading] = useState(false);

  // 라이트박스
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // 드래그-드롭 시각화
  const [dragActive, setDragActive] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await SitePhotoService.list(projectId);
      // 출장 일지 첨부 사진(category 'visit')은 일지 상세에서만 노출 — 갤러리 오염 방지.
      setPhotos(list.filter((p) => p.category !== 'visit'));
    } catch (e) {
      setError(e instanceof Error ? e.message : '사진 목록 조회 실패');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    const sorted = [...photos].sort(
      (a, b) => new Date(photoDate(b)).getTime() - new Date(photoDate(a)).getTime(),
    );
    if (filterCategory === 'all') return sorted;
    return sorted.filter((p) => p.category === filterCategory);
  }, [photos, filterCategory]);

  /** 날짜별로 그룹화된 사진 — { key: 'YYYY-MM-DD', items: SitePhoto[] }. */
  const groups = useMemo(() => {
    const map = new Map<string, SitePhoto[]>();
    filtered.forEach((p) => {
      const key = dayKey(photoDate(p));
      const arr = map.get(key) ?? [];
      arr.push(p);
      map.set(key, arr);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([key, items]) => ({ key, items }));
  }, [filtered]);

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const arr = Array.from(files).filter((f) => f.type.startsWith('image/'));
      if (arr.length === 0) {
        setSnack({ open: true, message: '이미지 파일만 업로드할 수 있습니다.', severity: 'error' });
        return;
      }
      setUploading(true);
      let okCount = 0;
      const failed: string[] = [];
      for (const f of arr) {
        try {
          await SitePhotoService.upload(projectId, {
            file: f,
            category: uploadCategory,
            // EXIF 못 읽으므로 파일의 lastModified 를 잠정 takenAt 으로 사용.
            takenAt: new Date(f.lastModified).toISOString(),
          });
          okCount += 1;
        } catch (e) {
          failed.push(`${f.name}: ${e instanceof Error ? e.message : String(e)}`);
        }
      }
      setUploading(false);
      if (okCount > 0) {
        setSnack({
          open: true,
          message:
            failed.length > 0
              ? `${okCount}장 업로드, ${failed.length}장 실패`
              : `${okCount}장 업로드되었습니다.`,
          severity: failed.length > 0 ? 'info' : 'success',
        });
        await refresh();
      } else if (failed.length > 0) {
        setSnack({
          open: true,
          message: `업로드 실패: ${failed[0]}`,
          severity: 'error',
        });
      }
    },
    [projectId, uploadCategory, refresh],
  );

  const handleSelectFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
    e.target.value = '';
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles],
  );

  const handleDelete = useCallback(
    async (photo: SitePhoto) => {
      if (!window.confirm(`'${photo.original_name || photo.filename}' 사진을 삭제할까요?`)) return;
      try {
        await SitePhotoService.delete(photo.id);
        setSnack({ open: true, message: '삭제되었습니다.', severity: 'success' });
        await refresh();
      } catch (e) {
        setSnack({
          open: true,
          message: e instanceof Error ? e.message : '삭제 실패',
          severity: 'error',
        });
      }
    },
    [refresh],
  );

  const openLightboxAt = (photo: SitePhoto) => {
    const idx = filtered.findIndex((p) => p.id === photo.id);
    if (idx >= 0) {
      setLightboxIndex(idx);
      setLightboxOpen(true);
    }
  };

  // 카테고리별 개수
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: photos.length };
    PHOTO_CATEGORIES.forEach((c) => (counts[c] = 0));
    photos.forEach((p) => {
      counts[p.category] = (counts[p.category] ?? 0) + 1;
    });
    return counts;
  }, [photos]);

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <PhotoIcon color="action" fontSize="small" />
          <Typography variant="h6" fontWeight={700}>
            현장 사진
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ({photos.length}장)
          </Typography>
        </Stack>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="업로드 카테고리">
          <ToggleButtonGroup
            value={uploadCategory}
            exclusive
            size="small"
            onChange={(_, v) => v && setUploadCategory(v)}
          >
            {PHOTO_CATEGORIES.map((c) => (
              <ToggleButton key={c} value={c} sx={{ px: 1, py: 0.25, fontSize: '0.72rem' }}>
                {PHOTO_CATEGORY_LABEL[c] ?? c}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Tooltip>
        <Tooltip title="새로고침">
          <span>
            <IconButton onClick={refresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Button
          variant="contained"
          startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? '업로드 중...' : '사진 업로드'}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={handleSelectFiles}
        />
      </Stack>

      {/* 카테고리 필터 칩 */}
      <Stack
        direction="row"
        spacing={0.75}
        sx={{ mb: 2, flexWrap: 'wrap', rowGap: 0.5 }}
      >
        <Chip
          label={`전체 (${categoryCounts.all ?? 0})`}
          size="small"
          color={filterCategory === 'all' ? 'primary' : 'default'}
          variant={filterCategory === 'all' ? 'filled' : 'outlined'}
          onClick={() => setFilterCategory('all')}
        />
        {PHOTO_CATEGORIES.map((c) => (
          <Chip
            key={c}
            label={`${PHOTO_CATEGORY_LABEL[c] ?? c} (${categoryCounts[c] ?? 0})`}
            size="small"
            color={filterCategory === c ? 'primary' : 'default'}
            variant={filterCategory === c ? 'filled' : 'outlined'}
            onClick={() => setFilterCategory(c)}
          />
        ))}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 드래그-드롭 영역 + 갤러리 */}
      <Box
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragActive(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragActive(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // child enter 도 leave 이벤트 발생 — currentTarget 밖일 때만 끔.
          if (e.currentTarget.contains(e.relatedTarget as Node)) return;
          setDragActive(false);
        }}
        onDrop={handleDrop}
        sx={{
          position: 'relative',
          border: '2px dashed',
          borderColor: dragActive ? 'primary.main' : 'transparent',
          borderRadius: 1,
          bgcolor: dragActive ? 'action.hover' : 'transparent',
          transition: 'background-color 120ms, border-color 120ms',
          minHeight: 200,
        }}
      >
        {dragActive && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              color: 'primary.main',
              fontWeight: 700,
              fontSize: 18,
              bgcolor: 'rgba(25, 118, 210, 0.08)',
              borderRadius: 1,
              zIndex: 1,
            }}
          >
            여기에 사진을 놓으세요 — 카테고리: {PHOTO_CATEGORY_LABEL[uploadCategory] ?? uploadCategory}
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : groups.length === 0 ? (
          <Box
            sx={{
              py: 8,
              textAlign: 'center',
              color: 'text.secondary',
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <PhotoIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
            <Typography variant="body2">
              {filterCategory === 'all'
                ? '아직 업로드된 사진이 없습니다.'
                : `'${PHOTO_CATEGORY_LABEL[filterCategory] ?? filterCategory}' 카테고리에 사진이 없습니다.`}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              파일을 끌어다 놓거나 우측 상단의 "사진 업로드" 버튼을 사용하세요.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            {groups.map(({ key, items }) => (
              <Box key={key}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="baseline"
                  sx={{ mb: 1, position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper', py: 0.5 }}
                >
                  <Typography variant="subtitle1" fontWeight={700}>
                    {formatDayHeader(key)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {items.length}장
                  </Typography>
                </Stack>
                <ImageList cols={6} rowHeight={140} gap={8} sx={{ m: 0 }}>
                  {items.map((p) => (
                    <ImageListItem
                      key={p.id}
                      sx={{
                        cursor: 'zoom-in',
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                        position: 'relative',
                        '&:hover .photo-actions': { opacity: 1 },
                        '&:hover .photo-caption': { opacity: 1 },
                      }}
                      onClick={() => openLightboxAt(p)}
                    >
                      {isVideoPhoto(p) ? (
                        // 동영상은 <img> 로 그리면 엑박 — 첫 프레임 썸네일로 표시.
                        <video
                          src={SitePhotoService.fileUrl(p.id)}
                          muted
                          preload="metadata"
                          style={{ objectFit: 'cover', width: '100%', height: '100%', display: 'block' }}
                        />
                      ) : (
                        <img
                          src={SitePhotoService.fileUrl(p.id)}
                          alt={p.original_name || p.filename}
                          loading="lazy"
                          style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                        />
                      )}
                      <ImageListItemBar
                        className="photo-caption"
                        sx={{
                          opacity: 0,
                          transition: 'opacity 120ms',
                          background:
                            'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0) 100%)',
                        }}
                        position="bottom"
                        title={
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'white',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                            }}
                          >
                            {PHOTO_CATEGORY_LABEL[p.category] ?? p.category}
                            {p.original_name ? ` · ${p.original_name}` : ''}
                          </Typography>
                        }
                      />
                      <Box
                        className="photo-actions"
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          display: 'flex',
                          gap: 0.25,
                          opacity: 0,
                          transition: 'opacity 120ms',
                          bgcolor: 'rgba(0,0,0,0.5)',
                          borderRadius: 1,
                          zIndex: 2,
                        }}
                      >
                        <Tooltip title="확대 보기">
                          <IconButton
                            size="small"
                            sx={{ color: 'white' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              openLightboxAt(p);
                            }}
                            aria-label="확대"
                          >
                            <ZoomInIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="다운로드">
                          <IconButton
                            size="small"
                            component="a"
                            href={SitePhotoService.fileUrl(p.id)}
                            download={p.original_name || p.filename}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: 'white' }}
                            onClick={(e) => e.stopPropagation()}
                            aria-label="다운로드"
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="삭제">
                          <IconButton
                            size="small"
                            sx={{ color: 'white' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(p);
                            }}
                            aria-label="삭제"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ImageListItem>
                  ))}
                </ImageList>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      <PhotoLightboxDialog
        open={lightboxOpen}
        photos={filtered}
        initialIndex={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default PhotoGalleryView;
